import { getToken } from './api.js';
import { returnParam, withReturn } from './gate.jsx';
import { useChannels, WA_PATH } from './phone.jsx';
import { GoogleButton } from './google.jsx';


/* ══════════════════════════════════════════════════════════════════════════
 * WELCOME — `02_Welcome.html`, the front door
 *
 * ⚠️ AL'S RULE, 22 SEPTEMBER 2026: *"if they're already logged in, they go
 * straight to the home page. They bypass the splash, the logins, everything if
 * they've already logged in, unless if they're logged out. Then, of course,
 * they sign back in."*
 *
 * That bypass is enforced in `main.jsx`, not here, and deliberately: a screen
 * that redirects itself has already rendered once, which on a phone is a flash
 * of a sign-up page in front of somebody who signed up months ago. The router
 * never mounts this component for a signed-in Eleater.
 *
 * ── ⚠️ TWO DOORS, AND THEY ARE NOT THE SAME DOOR ────────────────────────────
 *
 * Al, 16 September 2026: *"no we have 2 different sign ins. one by whatsapp and
 * one by email. why are you confusing this."* This screen has said so all
 * along, in two rows with their own subtitles:
 *
 *     WhatsApp — "Your number and your password"
 *     Email    — "Your address and your password"
 *
 * ⚠️⚠️ THE WHATSAPP ROW IS REMOVED — AL, 28 SEPTEMBER 2026, ON HIS OWN PHONE:
 * *"WhatsApp not working right now. What the fuck kind of message is that?
 * Jesus Christ, why do you even show it there for now? It shouldn't even be
 * shown until we've got it set up."*
 *
 * ⚠️ AND MY REASON FOR SHOWING IT WAS ANSWERING A DIFFERENT QUESTION. The note
 * that used to sit here argued that a greyed row "explains itself" and is not
 * the dead control this project keeps clearing out. True as far as it goes —
 * and completely beside the point, which is that the front door of Eleat
 * should not advertise something Eleat cannot do. **The first thing anybody
 * sees was an apology.** Same shape as the geocoder comment that justified
 * never calling Google inside Hong Kong, and the microphone comment that
 * justified swallowing a real failure: a sentence written to defend the code,
 * read afterwards as a decision.
 *
 * ⚠️ IT COMES BACK WHEN THE DOOR OPENS, NOT BEFORE. The row is deleted rather
 * than hidden behind a flag — RULE 3: never leave a code path that accepts the
 * old behaviour as well. When Meta business verification lands, the row is
 * rebuilt from `02_Welcome.html` (`.signrow`, the icon, *"Your number and your
 * password"*), which is where it came from in the first place.
 *
 * ⚠️ `.signrow.notready` STAYS IN `eleat.css` and is now unused. It is three
 * lines, it is the locked screen's own shape, and deleting it would mean
 * writing it again the day WhatsApp opens.
 *
 * ⚠️ AND THERE IS NO GOOGLE ROW. Al removed it on 16 September and it does not
 * come back until he approves it. `03_Google_SignIn.html` exists and is not
 * wired to anything.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ THE WHATSAPP PILL COMES BACK BY ITSELF — 8 Oct 2026. Al asked for the
   WhatsApp door to be ready for the day Meta approves his number. It is drawn
   only when GET /auth/channels says Eleat can send a WhatsApp code, which is
   true only once his Render rows name the approved template — so until that
   day the front door is exactly what he approved on 28 September: one pill. */
export default function Welcome() {
  const { whatsapp } = useChannels();
  /* ⚠️ A BELT-AND-BRACES CHECK, NOT THE REAL ONE. main.jsx decides. If this
     ever renders for somebody signed in, the router has a bug — and sending
     them to Home is the least annoying way for that bug to behave. */
  if (getToken()) {
    window.location.replace(returnParam());
    return null;
  }

  return (
    /* ══════════════════════════════════════════════════════════════════════
       ⚠️⚠️⚠️ THIS SCREEN WAS UNSTYLED AND AL SAW IT — 27 September 2026.

       *"once i get into the app i see the splash and then it takes me to
       welcome to eleat… the entire ui is wrong and look at the colours as
       well. wtf"*  His screenshot shows **blue underlined links** — the
       browser's own default for an `<a>` with no rule attached.

       ⚠️ THE CAUSE: FOUR CLASS NAMES THAT EXIST NOWHERE IN THE STYLESHEET.
       `.door`, `.welcome`, `.signin` and `.btnlabel` were written here and
       never added to `eleat.css`. `.door` is the sign-up button, so the two
       main controls on Eleat's front door had no styling at all.

       ⚠️ AND `.door` CAME FROM THE WRONG FILE. It belongs to
       `ELEAT_1_Eleater_App.html`, a landing page — a different screen for a
       different purpose. **The locked Welcome screen is `02_Welcome.html`**
       and it has never used that word. This is RULE 4 in its plainest form: a
       structure invented here instead of the one that already exists.

       ⚠️ SO THE MARKUP BELOW IS THE LOCKED SCREEN'S, CLASS FOR CLASS —
       `header` + `.lockup` · `.main` · `.copy` · `.signlist` of `.signrow`
       with `.ic` / `.sub` / `.go` · `footer` + `.signin`. Same names, same
       nesting, so the rules copied into `eleat.css` land where they should.

       ⚠️ IT IS THE SAME FAULT AS `.ftheart` vs `.fheart` — a contrast fix
       written against class names that did not exist, which CLAUDE.md records
       under RULE 6. **The file looked finished and not one rule matched.**
       ══════════════════════════════════════════════════════════════════════ */
    <main className="screen welcome" aria-label="Eleat welcome screen">
      {/* ⚠️ THE REAL LOCKUP, WHICH WAS SITTING IN `web/public` UNUSED.
          `02_Welcome.html` header is one `eleat_lockup.png` at 132px — the
          mark and the wordmark drawn together, in their own proportions. This
          screen was stacking `eleat_mark.png` and `eleat_word.png` by hand,
          which is a second version of a lockup that already exists. */}
      <header>
        <img className="lockup" src="/eleat_lockup.png" alt="Eleat" />
      </header>

      <section className="main">
        <div className="copy">
          <h1>Welcome to Eleat</h1>
          <p>Join to discover real-time deals around you.</p>
        </div>

        {/* ⚠️ ONE DOOR ON SCREEN TODAY, AND THAT IS AL'S CALL OF 28 SEPTEMBER
            2026 — see the note at the top of this file. Eleat still HAS two
            doors; the WhatsApp one is not advertised until it opens. The
            locked screen's `.signlist` shape is unchanged: an icon, a name,
            what it will ask for, and a chevron. */}
        {/* ⚠️ `02_Welcome.html`'s JOINING BUTTON, NOT ITS SIGN-IN ROW —
            30 September 2026. The locked screen has two shapes: `.actions`
            (the pills — "Sign up with …") while joining, and `.signlist`
            (the "Email · Your address and your password" rows) only in its
            SIGNING-IN mode, under "Welcome back". This screen had the sign-in
            row standing in for the joining button. With WhatsApp off the front
            door (Al, 28 Sep) the locked joining block is this one pill. */}
        <div className="actions">
          {whatsapp && (
            <a className="btn primary" href={withReturn('?join=1&by=whatsapp', returnParam())}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="#0b141a" style={{ flex: 'none' }}
                aria-hidden="true"><path d={WA_PATH} /></svg>
              <span>Sign up with WhatsApp</span>
            </a>
          )}
          {/* the screen's one orange button (Al-Noor, 30 Oct 2026: "put the join
              with email in orange… it is our colours") — Google beneath it,
              outlined, the same size */}
          <a className="btn joinmain" href={withReturn('?join=1', returnParam())}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}
              aria-hidden="true">
              <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
              <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
            </svg>
            <span>Join with email</span>
          </a>
          {/* 02_Welcome.html's "Sign up with Google" pill — Google's own button,
              drawn only once eleat-api has GOOGLE_CLIENT_ID (28 Oct 2026) */}
          <GoogleButton mode="signup" />
        </div>
      </section>

      <footer>
        Already on Eleat? <a className="signin" href={withReturn('?signin=1', returnParam())}>Sign in</a>
      </footer>
    </main>
  );
}
